import React from "react";
import styled from "styled-components";
import { ReactComponent as Feather } from "../../../../assets/landing/patria-feather.svg";

/**
 * Section 4: the dark "We are Patria" panel. The supplied 9:16 brand creative is
 * shown at its native aspect here rather than cropped — this is the layout it was
 * made for. See Hero.styles.ts for the px-not-rem note.
 */
const Wrapper = styled.section`
  position: relative;
  overflow: hidden;
  background: var(--brand-ink);
  color: #fff;

  .inner {
    position: relative;
    margin: 0 auto;
    max-width: 1152px;
    display: grid;
    gap: 40px;
    padding: 80px 20px;
  }

  /* Grid children default to min-width:auto and so refuse to shrink below their
   * min-content. That let the 3-up metrics row force this column ~57px wider
   * than a 320px viewport, clipping the panel. */
  .inner > * {
    min-width: 0;
  }

  .media {
    position: relative;
  }

  .frame {
    position: relative;
    overflow: hidden;
    border-radius: 32px;
    border: 1px solid rgba(255, 255, 255, 0.1);
    box-shadow: 0 30px 60px -30px rgba(0, 0, 0, 0.6);
  }

  .frame img {
    display: block;
    width: 100%;
    aspect-ratio: 9 / 16;
    object-fit: cover;
  }

  @supports not (aspect-ratio: 9 / 16) {
    .frame img {
      height: auto;
    }
  }

  .badge {
    display: none;
    position: absolute;
    bottom: -20px;
    right: -20px;
    transform: rotate(-4deg);
    border-radius: 16px;
    background: var(--brand-red);
    padding: 12px 16px;
    font-size: 14px;
    font-weight: 700;
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.3);
  }

  .eyebrow {
    margin: 0 0 20px;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.2em;
    color: var(--brand-red);

    svg {
      width: 16px;
      height: 16px;
    }
  }

  .heading {
    margin: 0;
    font-family: var(--brand-display);
    font-size: 36px;
    font-weight: 800;
    line-height: 1.05;
    letter-spacing: -0.025em;
    color: #fff;

    .accent {
      color: var(--brand-red);
    }

    /* block so "tomorrow's" gets a line to itself, with "opportunities." below.
     * Left inline it shared the line with "expenses and" and overflowed the
     * column. */
    .soft {
      display: block;
      font-style: italic;
      color: rgba(255, 255, 255, 0.9);
    }
  }

  .copy {
    margin: 20px 0 0;
    font-size: 18px;
    line-height: 1.625;
    color: rgba(255, 255, 255, 0.75);

    strong {
      font-weight: 600;
      color: #fff;
    }
  }

  .metrics {
    margin-top: 32px;
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;
  }

  .metric {
    border-radius: 16px;
    border: 1px solid rgba(255, 255, 255, 0.1);
    background: rgba(255, 255, 255, 0.05);
    padding: 16px;
    text-align: center;
  }

  .metric-value {
    display: block;
    font-family: var(--brand-display);
    font-size: 24px;
    font-weight: 800;
    color: #fff;
  }

  .metric-label {
    display: block;
    margin-top: 4px;
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: rgba(255, 255, 255, 0.5);
  }

  /* three metrics across a 320px screen leaves ~85px each; tighten the padding
   * and type so the labels and values still fit rather than being clipped */
  @media screen and (max-width: 400px) {
    .metric {
      padding: 12px 6px;
    }

    .metric-value {
      font-size: 20px;
    }

    .metric-label {
      font-size: 10px;
      letter-spacing: 0.04em;
    }
  }

  @media screen and (min-width: 640px) {
    .inner {
      padding: 80px 32px;
    }

    .badge {
      display: block;
    }

    .heading {
      font-size: 48px;
    }

    .metric-value {
      font-size: 30px;
    }
  }

  @media screen and (min-width: 1024px) {
    .inner {
      grid-template-columns: 0.9fr 1.1fr;
      align-items: center;
      gap: 56px;
      padding: 112px 32px;
    }
  }
`;

export type Metric = { value: string; label: string };

const AboutPatria = ({
  image,
  alt,
  badge,
  metrics,
}: {
  image: string;
  alt: string;
  badge: string;
  metrics: Metric[];
}) => {
  return (
    <Wrapper className="section-lp-about">
      <div className="inner">
        <div className="media">
          <div className="frame">
            <img src={image} alt={alt} loading="lazy" />
          </div>
          <div className="badge">{badge}</div>
        </div>

        <div className="content">
          <p className="eyebrow">
            <Feather />
            We are Patria
          </p>
          <h2 className="heading">
            We bridge the gap between{" "}
            <span className="accent">today&apos;s</span> expenses and{" "}
            <span className="soft">tomorrow&apos;s</span> opportunities.
          </h2>
          <p className="copy">
            Patria Lending helps the <strong>40+ million Americans</strong> who
            don&apos;t have access to short-term credit — people who deserve a
            real option when unexpected expenses hit. Sign your loan and money
            can land in <strong>minutes</strong>.
          </p>
          <div className="metrics">
            {metrics.map(({ value, label }) => (
              <div className="metric" key={label}>
                <span className="metric-value">{value}</span>
                <span className="metric-label">{label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Wrapper>
  );
};

export default AboutPatria;
